import { useCallback, useState } from 'react';
import CookiesUtils from '@utils/CookiesUtils';
import { TERRITORY_SELECTION_COOKIE } from '../../../helpers/territories';
/**
 * Load initial territory selections from cookies
 */
const loadInitialSelections = () => {
    try {
        const storedSelections = CookiesUtils.getCookie(TERRITORY_SELECTION_COOKIE);
        if (storedSelections) {
            return JSON.parse(storedSelections);
        }
        return {};
    }
    catch (error) {
        console.error('Error loading territory selections from cookies:', error);
    }
    return {};
};
/**
 * Custom hook to manage territory selection with cookie persistence per subdomain
 * @returns Object with functions to manage territory selections for any subdomain
 */
export default function useTerritorySelection() {
    // State to store territory selections for all subdomains
    const [territorySelections, setTerritorySelections] = useState(loadInitialSelections);
    /**
     * Get territory selection for a specific subdomain from state
     * @param subdomain - The subdomain to get territories for
     * @returns Array of selected territory IDs for the subdomain
     */
    const getSelectedTerritories = useCallback((subdomain) => {
        const subdomainSelections = territorySelections[subdomain] || [];
        // Ensure we have valid territory IDs
        return subdomainSelections.filter((id) => typeof id === 'number' && !Number.isNaN(id));
    }, [territorySelections]);
    /**
     * Save territory selection to both state and cookies for specific subdomain
     * @param territories - Array of territory IDs to save
     * @param subdomain - The subdomain to save territories for
     */
    const saveToStorage = useCallback((territories, subdomain) => {
        try {
            // Update state immediately for reactive UI
            setTerritorySelections((prev) => (Object.assign(Object.assign({}, prev), { [subdomain]: territories })));
            const existingSelections = CookiesUtils.getCookie(TERRITORY_SELECTION_COOKIE);
            let allSelections = {};
            if (existingSelections) {
                allSelections = JSON.parse(existingSelections);
            }
            allSelections[subdomain] = territories;
            CookiesUtils.setCookie(TERRITORY_SELECTION_COOKIE, JSON.stringify(allSelections));
        }
        catch (error) {
            console.error('Error saving territory selection to cookies:', error);
        }
    }, [setTerritorySelections]);
    /**
     * Toggle territory selection - add if not present, remove if present
     * @param territoryId - ID of the territory to toggle
     * @param subdomain - The subdomain to toggle territory for
     */
    const toggleTerritory = useCallback((territoryId, subdomain) => {
        const currentSelections = getSelectedTerritories(subdomain);
        const isCurrentlySelected = currentSelections.includes(territoryId);
        let newSelection;
        if (isCurrentlySelected) {
            newSelection = currentSelections.filter((id) => id !== territoryId);
        }
        else {
            newSelection = [...currentSelections, territoryId];
        }
        saveToStorage(newSelection, subdomain);
    }, [getSelectedTerritories, saveToStorage]);
    /**
     * Clear territory selections for specific subdomain
     * @param subdomain - The subdomain to clear selections for
     */
    const clearSelection = useCallback((subdomain) => {
        // Store explicit empty array (not delete) so the init-on-first-load
        // logic can distinguish "user cleared" from "no cookie yet"
        saveToStorage([], subdomain);
    }, [saveToStorage]);
    /**
     * Set multiple territories at once for specific subdomain
     * @param territoryIds - Array of territory IDs to select
     * @param subdomain - The subdomain to set territories for
     */
    const setMultipleTerritories = useCallback((territoryIds, subdomain) => {
        const validIds = territoryIds.filter((id) => typeof id === 'number' && !Number.isNaN(id));
        saveToStorage(validIds, subdomain);
    }, [saveToStorage]);
    // Removes the subdomain key from the cookie entirely (distinct from clearSelection which
    // stores [] to prevent re-init). Used when all territories are selected so the server
    // skips the territory subquery — the UI still shows all selected via React state, and on
    // next load the missing key triggers auto-select-all again.
    const removeStoredSelection = useCallback((subdomain) => {
        setTerritorySelections((prev) => {
            const next = Object.assign({}, prev);
            delete next[subdomain];
            return next;
        });
        try {
            const existingSelections = CookiesUtils.getCookie(TERRITORY_SELECTION_COOKIE);
            let allSelections = {};
            if (existingSelections) {
                allSelections = JSON.parse(existingSelections);
            }
            delete allSelections[subdomain];
            CookiesUtils.setCookie(TERRITORY_SELECTION_COOKIE, JSON.stringify(allSelections));
        }
        catch (error) {
            console.error('Error removing territory selection from cookies:', error);
        }
    }, [setTerritorySelections]);
    return {
        territorySelections,
        getSelectedTerritories,
        toggleTerritory,
        clearSelection,
        setMultipleTerritories,
        removeStoredSelection
    };
}
